import { useNavigate } from "@tanstack/react-router";
import {
  AlertOctagonIcon,
  ArchiveRestoreIcon,
  CheckSquare2Icon,
  ChevronsUpIcon,
  CircleIcon,
  ContactIcon,
  CopyIcon,
  CopyPlusIcon,
  DollarSignIcon,
  FileCheck,
  FileCheckIcon,
  FlaskConicalIcon,
  LayersIcon,
  PackageIcon,
  PencilIcon,
  TagIcon,
  TagsIcon,
  TestTubeIcon,
  TrashIcon,
  UploadIcon,
  User2Icon,
  XCircleIcon,
  XSquareIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useCopyToClipboard } from "react-use";

import { trpc } from "@blis/trpc/react";

import { useCommandMenu } from "@/components/CommandMenu";
import { RoleCondition } from "@/components/RoleCondition";
import { CommandMenu } from "@/components/ui/command";
import { StatusIcon } from "@/components/ui/icons";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { useAuth } from "@/features/auth/hooks";
import { navigateAndShowTestRunDialog } from "@/features/runs/components/store";
import { usePrintLabel } from "@/lib/labels";
import type { TAccessionStatus } from "@/lib/types";
import { mutationResponse } from "@/utils/trpc";
import { useNavLocationState } from "@/utils/useNavStack";
import { useAccession } from "./AccessionProvider";
import { RelatedAccessionsCommand } from "./commands/RelatedAccessionsCommand";
import { SetPriorityCommands } from "./commands/SetPriorityCommands";
import { SetStatusCommands } from "./commands/SetStatusCommands";
import { type DialogName, useSetDialogState } from "./detail/state";

export function AccessionCommands() {
  const { page, pages, setPages, setOpen } = useCommandMenu();
  const { accession, samples } = useAccession();
  const actions = useAccessionCommands();
  const { printSpecimenLabels, printArchiveLabels } = usePrintLabel();

  if (!accession) {
    return null;
  }

  return (
    <>
      {!page && (
        <CommandMenu.Section heading={`Accession ${accession?.id}`}>
          <RoleCondition
            roles={["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"]}
          >
            <AccessionCondition status={["cancelled"]}>
              <CommandMenu.Item
                onSelect={() => actions.restore()}
                value="Restore accession"
              >
                <ArchiveRestoreIcon />
                Restore accession
              </CommandMenu.Item>
            </AccessionCondition>
            <AccessionCondition not={["cancelled"]}>
              {accession.status === "final" ? (
                <CorrectedReportCommand />
              ) : accession.status === "completed" ? (
                <>
                  <GenerateReportCommand />
                  <HoldCommand />
                </>
              ) : accession.status === "in_progress" ? (
                <>
                  <EnterResultsCommand />
                  <HoldCommand />
                </>
              ) : accession.status === "received" ? (
                <>
                  <CreateTestRunCommand valueSuffix="2" />
                  <EnterResultsCommand />
                  <HoldCommand />
                </>
              ) : (
                <>
                  <ReceiveAccessionCommand />
                  <HoldCommand />
                </>
              )}
              <CommandMenu.Item
                value="edit accession"
                onSelect={() => actions.editAccession()}
              >
                <PencilIcon />
                Edit accession
                <CommandMenu.Shortcut>D</CommandMenu.Shortcut>
              </CommandMenu.Item>
              <CommandMenu.Item
                value="upload file"
                onSelect={() => actions.upload()}
              >
                <UploadIcon />
                Upload file
                <CommandMenu.Shortcut>Shift+U</CommandMenu.Shortcut>
              </CommandMenu.Item>
              <CommandMenu.Item
                value="view related"
                onSelect={() => actions.viewRelated()}
              >
                <LayersIcon />
                View related
                <CommandMenu.Shortcut>Shift+R</CommandMenu.Shortcut>
              </CommandMenu.Item>
              <CommandMenu.Item
                value="print labels"
                onSelect={() => actions.printLabels()}
              >
                <TagIcon />
                Print labels
                <CommandMenu.Shortcut>L L</CommandMenu.Shortcut>
              </CommandMenu.Item>
              <CommandMenu.Item
                value="print archive labels"
                onSelect={() => actions.printArchiveLabels()}
              >
                <TagIcon />
                Print archive labels
                <CommandMenu.Shortcut>L A</CommandMenu.Shortcut>
              </CommandMenu.Item>
              {/* <CommandMenu.Item
                value="clone accession"
                onSelect={() => actions.clone()}
              >
                <CopyPlusIcon />
                Clone accession
              </CommandMenu.Item>
              <CommandMenu.Item
                value="stage for instrument"
                onSelect={() => actions.stageForInstrument()}
              >
                <CpuIcon />
                Stage for instrument
              </CommandMenu.Item> */}
              <RefreshStatusCommand />
              <CommandMenu.Item
                value="set status"
                onSelect={() => actions.openSetStatusCommand()}
              >
                <StatusIcon status={accession.status} noColor />
                Set status
                <CommandMenu.Shortcut>S S</CommandMenu.Shortcut>
              </CommandMenu.Item>
              <CommandMenu.Item
                value="set priority"
                onSelect={() => actions.openSetPriorityCommand()}
              >
                <ChevronsUpIcon />
                Set priority
                <CommandMenu.Shortcut>S P</CommandMenu.Shortcut>
              </CommandMenu.Item>
              <CommandMenu.Item
                value="clear all results"
                onSelect={() => actions.clearAllResults()}
              >
                <XSquareIcon />
                Clear all results
              </CommandMenu.Item>
            </AccessionCondition>
          </RoleCondition>
          <CommandMenu.Item
            value="copy to clipboard"
            onSelect={() => actions.copyToClipboard()}
          >
            <CopyIcon />
            Copy to clipboard
          </CommandMenu.Item>
          <RoleCondition roles={["blis_lab_tech", "blis_lab_manager"]}>
            <AccessionCondition not={["cancelled"]}>
              <CommandMenu.Item
                value="cancel accession"
                onSelect={() => actions.cancel()}
              >
                <XCircleIcon />
                Cancel accession
              </CommandMenu.Item>
            </AccessionCondition>
          </RoleCondition>
          <RoleCondition roles={["blis_lab_manager"]}>
            <CommandMenu.Item
              value="delete accession"
              onSelect={() => actions.delete()}
            >
              <TrashIcon color="red" />
              Delete accession
            </CommandMenu.Item>
          </RoleCondition>
        </CommandMenu.Section>
      )}
      {page?.id === "edit" && (
        <CommandMenu.Section>
          <CommandMenu.Item
            value="add tests"
            onSelect={() => actions.addTests()}
          >
            <FlaskConicalIcon />
            Add tests
          </CommandMenu.Item>
          <CommandMenu.Item
            value="add sample"
            onSelect={() => actions.addSample()}
          >
            <TestTubeIcon />
            Add sample
          </CommandMenu.Item>
          <CommandMenu.Item
            value="edit donor information"
            onSelect={() => actions.editDonor()}
          >
            <User2Icon />
            Edit donor information
          </CommandMenu.Item>
          <CommandMenu.Item
            value="move accession to donor"
            onSelect={() => actions.moveToSubject()}
          >
            <CopyPlusIcon />
            Move accession to donor
          </CommandMenu.Item>
          <CommandMenu.Item
            value="edit client information"
            onSelect={() => actions.editClient()}
          >
            <ContactIcon />
            Edit client information
          </CommandMenu.Item>
          <CommandMenu.Item
            value="Edit accession info"
            onSelect={() => actions.editAdditional()}
          >
            <PackageIcon />
            Edit accession info
          </CommandMenu.Item>
          <RoleCondition roles={["blis_lab_manager"]}>
            <CommandMenu.Item
              value="edit billing"
              onSelect={() => actions.editBilling()}
            >
              <DollarSignIcon />
              Edit billing
            </CommandMenu.Item>
          </RoleCondition>
        </CommandMenu.Section>
      )}
      {page?.id === "copy" && (
        <CommandMenu.Section>
          <CommandMenu.Item
            value="copy accession id"
            onSelect={() => actions.copyAccessionId()}
          >
            <CopyIcon />
            Copy accession ID
            <CommandMenu.Shortcut>C I</CommandMenu.Shortcut>
          </CommandMenu.Item>
          <CommandMenu.Item
            value="copy donor id"
            onSelect={() => actions.copyCaseId()}
          >
            <CopyIcon />
            Copy donor ID
            <CommandMenu.Shortcut>C C</CommandMenu.Shortcut>
          </CommandMenu.Item>
        </CommandMenu.Section>
      )}
      {(page?.id === "print-labels" || page?.id === "print-archive-labels") && (
        <CommandMenu.Section>
          <CommandMenu.Item
            value="print all"
            onSelect={async () => {
              if (page?.id === "print-archive-labels") {
                await printArchiveLabels(accession, samples);
              } else {
                await printSpecimenLabels(accession, samples);
              }
              setOpen(false);
            }}
          >
            <TagsIcon />
            Print all
          </CommandMenu.Item>
          {samples.map((sample) => (
            <CommandMenu.Item
              key={sample.id}
              value={`${sample.tube.name} ${sample.displayId}`}
              onSelect={() =>
                setPages((pages) => [
                  ...pages,
                  { id: sample.id, label: sample.tube.name },
                ])
              }
              className="w-full"
            >
              <TagIcon />
              <div className="flex items-center justify-between w-full">
                <span>{sample.tube.name}</span>
                <Text muted>{sample.displayId}</Text>
              </div>
            </CommandMenu.Item>
          ))}
        </CommandMenu.Section>
      )}
      {samples.map((s) => s.tube.name).includes(page?.label) && (
        <CommandMenu.Section>
          {[1, 2, 3, 4, 5].map((num) => (
            <CommandMenu.Item
              key={num}
              value={`print ${num}`}
              onSelect={async () => {
                // Find the corresponding samples
                const legacySample = samples.find((s) => s.id === page.id);

                if (legacySample) {
                  if (pages[0]?.id === "print-archive-labels") {
                    // Smart archive printing - automatically handles feature flags
                    await printArchiveLabels(
                      accession,
                      Array(num).fill(legacySample),
                    );
                  } else {
                    // Smart specimen printing - automatically handles feature flags
                    await printSpecimenLabels(
                      accession,
                      Array(num).fill(legacySample),
                    );
                  }
                }
                setOpen(false);
              }}
            >
              <TagIcon />
              Print {num}
            </CommandMenu.Item>
          ))}
        </CommandMenu.Section>
      )}
      {page?.id === "related" && <RelatedAccessionsCommand />}
      {page?.id === "set-status" && <SetStatusCommands />}
      {page?.id === "set-priority" && <SetPriorityCommands />}
    </>
  );
}

function EnterResultsCommand() {
  const { enterResults } = useAccessionCommands();
  const { user } = useAuth();
  if (!user?.canHandleResults()) {
    return null;
  }
  return (
    <CommandMenu.Item onSelect={() => enterResults()} value="enter results">
      <CheckSquare2Icon />
      Enter results
      <CommandMenu.Shortcut>E</CommandMenu.Shortcut>
    </CommandMenu.Item>
  );
}

function CorrectedReportCommand() {
  const { generateReport } = useAccessionCommands();
  const { user } = useAuth();
  if (!user?.canHandleResults()) {
    return null;
  }
  return (
    <CommandMenu.Item
      onSelect={() => generateReport()}
      value="add corrected report"
    >
      <FileCheckIcon />
      Add corrected report
      <CommandMenu.Shortcut>P</CommandMenu.Shortcut>
    </CommandMenu.Item>
  );
}

function GenerateReportCommand() {
  const { generateReport } = useAccessionCommands();
  const { user } = useAuth();
  if (!user?.canHandleResults()) {
    return null;
  }
  return (
    <CommandMenu.Item onSelect={() => generateReport()} value="generate report">
      <FileCheck />
      Generate report
      <CommandMenu.Shortcut>P</CommandMenu.Shortcut>
    </CommandMenu.Item>
  );
}

function CreateTestRunCommand({ valueSuffix }: { valueSuffix?: string }) {
  const { createTestRun } = useAccessionCommands();
  return (
    <CommandMenu.Item
      onSelect={() => createTestRun()}
      value={`create test run${valueSuffix ? ` ${valueSuffix}` : ""}`}
    >
      <FlaskConicalIcon />
      Create test run
      <CommandMenu.Shortcut>U</CommandMenu.Shortcut>
    </CommandMenu.Item>
  );
}

function ReceiveAccessionCommand() {
  const { receive } = useAccessionCommands();
  return (
    <CommandMenu.Item onSelect={() => receive()} value="receive accession">
      <CircleIcon />
      Receive accession
      <CommandMenu.Shortcut>R</CommandMenu.Shortcut>
    </CommandMenu.Item>
  );
}

function HoldCommand() {
  const { hold } = useAccessionCommands();
  return (
    <CommandMenu.Item onSelect={() => hold()} value="create a hold">
      <AlertOctagonIcon />
      Create a hold
      <CommandMenu.Shortcut>H</CommandMenu.Shortcut>
    </CommandMenu.Item>
  );
}

function RefreshStatusCommand() {
  const { refreshStatus } = useAccessionCommands();
  const { accession } = useAccession();
  return (
    <CommandMenu.Item onSelect={() => refreshStatus()} value="refresh status">
      <StatusIcon status={accession.status} noColor />
      Refresh status
    </CommandMenu.Item>
  );
}

function AccessionCondition({
  children,
  status,
  not,
}: {
  children: React.ReactNode;
  status?: TAccessionStatus[];
} & { children: React.ReactNode; not?: TAccessionStatus[] }) {
  const { accession } = useAccession();

  if (status && accession.status && status.includes(accession.status)) {
    return <>{children}</>;
  }

  if (not && accession.status && !not.includes(accession.status)) {
    return <>{children}</>;
  }

  return null;
}

export function useAccessionCommands() {
  const { accession, testOrders } = useAccession();
  const { setOpen, setPages } = useCommandMenu();
  const cbRef = useRef<() => void>(undefined);

  const [, copyToClipboard] = useCopyToClipboard();
  const setDialogState = useSetDialogState();
  const navigate = useNavigate();
  const { href, label } = useNavLocationState();

  const openFirstResultEntry = useCallback(() => {
    const order = testOrders?.find((to) => !to.isReflex);
    setDialogState("enterResults", order ? { orderId: order.id } : null);
  }, [setDialogState, testOrders]);

  useEffect(() => {
    cbRef.current = openFirstResultEntry;
  }, [openFirstResultEntry]);

  const openDialogAndCloseCommand = useCallback(
    (dialog: DialogName) => () => {
      setOpen(false);
      setDialogState(dialog, true);
    },
    [setDialogState, setOpen],
  );

  const preselectedKeys = useMemo(() => {
    if (!testOrders) return [];
    return testOrders.map((to) => to.testId);
  }, [testOrders]);

  const refreshStatus = useRefreshStatus();
  const setStatus = useSetStatus();
  const setPriority = useSetPriority();
  const updateTimeZone = useUpdateTimeZone();

  return useMemo(
    () => ({
      cancel: openDialogAndCloseCommand("cancel"),
      clone: openDialogAndCloseCommand("clone"),
      delete: openDialogAndCloseCommand("delete"),
      editBilling: openDialogAndCloseCommand("editBilling"),
      editClient: openDialogAndCloseCommand("editClient"),
      editDonor: openDialogAndCloseCommand("editDonor"),
      moveToSubject: openDialogAndCloseCommand("moveToSubject"),
      editSamples: openDialogAndCloseCommand("editSamples"),
      editTests: openDialogAndCloseCommand("editTests"),
      editAdditional: openDialogAndCloseCommand("editAdditional"),
      addTests: openDialogAndCloseCommand("addTests"),
      addSample: openDialogAndCloseCommand("addSample"),
      hold: openDialogAndCloseCommand("hold"),
      sampleDetails: openDialogAndCloseCommand("samplesTable"),
      stageForInstrument: openDialogAndCloseCommand("instrument"),
      clearAllResults: openDialogAndCloseCommand("clearAll"),
      receive: openDialogAndCloseCommand("receive"),
      restore: openDialogAndCloseCommand("restore"),
      unreceive: openDialogAndCloseCommand("unreceive"),
      upload: openDialogAndCloseCommand("upload"),

      copyAccessionId: () => {
        copyToClipboard(accession.id.toString());
        toast.info("Accession ID copied to clipboard");
        setOpen(false);
      },
      copyCaseId: () => {
        copyToClipboard(accession.caseId);
        toast.info("Donor ID copied to clipboard");
        setOpen(false);
      },
      copyToClipboard: () => {
        setPages([{ id: "copy", label: "Copy to clipboard" }]);
        setOpen(true);
      },
      createTestRun(testIds = preselectedKeys) {
        navigateAndShowTestRunDialog({
          location,
          navigate,
          preselectedKeys: testIds,
        });
      },
      editAccession() {
        setPages([{ id: "edit", label: "Edit accession" }]);
        setOpen(true);
      },
      enterResults: () => {
        setOpen(false);
        cbRef.current?.();
      },
      generateReport: () => {
        setOpen(false);
        navigate({
          to: "/accessions/$id/report",
          params: { id: accession.id },
          state: { href, label },
        });
      },
      printLabels() {
        setPages([{ id: "print-labels", label: "Print labels" }]);
        setOpen(true);
      },
      printArchiveLabels() {
        setPages([
          { id: "print-archive-labels", label: "Print archive labels" },
        ]);
        setOpen(true);
      },
      viewRelated() {
        setPages([
          {
            id: "related",
            label: "Related",
            hideEmpty: true,
          },
        ]);
        setOpen(true);
      },
      refreshStatus() {
        refreshStatus();
        setOpen(false);
      },
      setStatus(status: TAccessionStatus) {
        setStatus(status);
        setOpen(false);
      },
      openSetStatusCommand() {
        setPages([{ id: "set-status", label: "Set status" }]);
        setOpen(true);
      },
      setPriority(priority: "STAT" | "ROUTINE") {
        setPriority(priority);
        setOpen(false);
      },
      openSetPriorityCommand() {
        setPages([{ id: "set-priority", label: "Set priority" }]);
        setOpen(true);
      },
      setTimeZone(timezone: string) {
        updateTimeZone(timezone);
        setOpen(false);
      },
    }),
    [
      accession.id,
      navigate,
      preselectedKeys,
      openDialogAndCloseCommand,
      refreshStatus,
      setOpen,
      setPages,
      setStatus,
      setPriority,
      updateTimeZone,
      copyToClipboard,
      accession.caseId,
      href,
      label,
    ],
  );
}

function useRefreshStatus() {
  const { accession } = useAccession();
  const refresh = trpc.accession.refreshStatus.useMutation(
    mutationResponse({
      errorTitle: "Error refreshing accession status",
      onSuccess(data) {
        if (data?.length === 0) {
          toast.success("Accession status already up to date");
        } else {
          toast.success("Accession status refreshed");
        }
      },
    }),
  );
  const fn = useCallback(() => {
    refresh.mutate({
      accessions: [{ id: accession.id, status: accession.status }],
    });
  }, [accession.id, accession.status, refresh]);

  return fn;
}

function useSetStatus() {
  const { setOpen } = useCommandMenu();
  const { accession } = useAccession();
  const setDialogState = useSetDialogState();

  const setStatus = trpc.accession.setStatus.useMutation(
    mutationResponse({
      errorTitle: "Error setting accession status",
      successMessage: "Accession status set",
      onSuccess: () => {
        setOpen(false);
      },
    }),
  );

  const fn = useCallback(
    (status: TAccessionStatus) => {
      setStatus.mutate({
        accessionId: accession.id,
        status,
      });
    },
    [accession.id, setStatus],
  );

  const updateStatusWrapper = useCallback(
    (newStatus: TAccessionStatus) => {
      if (newStatus === "received" && accession.status === "new") {
        setOpen(false);
        setDialogState("receive", true);
      } else if (newStatus === "new" && accession.received) {
        setOpen(false);
        setDialogState("unreceive", true);
      } else if (
        newStatus === "cancelled" &&
        accession.status !== "cancelled"
      ) {
        setOpen(false);
        setDialogState("cancel", true);
      } else {
        fn(newStatus);
      }
    },
    [accession.status, accession.received, fn, setDialogState, setOpen],
  );

  return updateStatusWrapper;
}

function useSetPriority() {
  const { setOpen } = useCommandMenu();
  const { accession } = useAccession();

  const setPriority = trpc.accession.setPriority.useMutation(
    mutationResponse({
      errorTitle: "Error setting accession priority",
      successMessage: "Accession priority set",
      onSuccess: () => {
        setOpen(false);
      },
    }),
  );

  const fn = useCallback(
    (priority: "STAT" | "ROUTINE") => {
      setPriority.mutate({
        accessionId: accession.id,
        priority,
      });
    },
    [accession.id, setPriority],
  );

  return fn;
}

function useUpdateTimeZone() {
  const { setOpen } = useCommandMenu();
  const { accession } = useAccession();

  const mutate = trpc.accession.updateTimeZone.useMutation(
    mutationResponse({
      errorTitle: "Error updating time zone",
      successMessage: "Time zone updated",
      onSuccess: () => {
        setOpen(false);
      },
    }),
  );

  const fn = useCallback(
    (timezone: string) => {
      mutate.mutate({
        accessionId: accession.id,
        timezone,
      });
    },
    [accession.id, mutate],
  );

  return fn;
}
